import { computed, inject, Signal } from '@angular/core';
import { AuthStateService } from './auth-state.service';
import { PrivilegeName } from '../../models/privileges';

/**
 * Signal-reactive privilege check. Must be called in an injection
 * context (field initializer, constructor, or runInInjectionContext).
 *
 * Reads from AuthStateService.user, which is populated by
 * AuthService.loadCurrentUser() on app startup. Because it's a
 * computed signal, it stays correct regardless of whether the user
 * signal is loaded before or after the component instantiates — no
 * need to `await` getActiveUser() in ngOnInit or ionViewWillEnter.
 *
 * Replaces the older pattern:
 *     // Race-prone: activeUser may still be null if the page renders
 *     // before AuthService.loadToken() finishes its /users/me fetch.
 *     protected activeUser: User | null = null;
 *     async ngOnInit() {
 *         this.activeUser = await this.userService.getActiveUser();
 *     }
 *     // Template: *ngIf="activeUser?.hasPrivilege('preset_edit')"
 *
 * with:
 *     protected canEditPreset = usePrivilege(Privileges.PresetEdit);
 *     // Template: *ngIf="canEditPreset()"
 *
 * @param name The privilege to check for. Use the Privileges constant
 *             (e.g. Privileges.PresetEdit) — never a raw string — so
 *             typos are caught at compile time and IDE rename works.
 * @returns A Signal&lt;boolean&gt; that's true when the current user has
 *          the privilege, false otherwise (including while the user
 *          signal is still loading and when there's no user).
 */
export const usePrivilege = (name: PrivilegeName): Signal<boolean> => {
    const authState = inject(AuthStateService);
    return computed(() => authState.user()?.hasPrivilege(name) ?? false);
};
